import { useEffect, useState } from 'react';
import {
  Calendar as CalendarIcon, Clock, Video, Loader2, CheckCircle2, XCircle,
  RefreshCw, User as UserIcon, Mail, Phone, X, FileText,
} from 'lucide-react';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';
import { formatDateTime, formatTime } from '@/lib/dateFormat';
import { DateInput } from '@/components/ui/DateInput';

// ────────────────────────────────────────────────────────────────────────────
// Shared demo-booking card — mirrors the vendor MeetingsTab card so the
// super-admin demo lifecycle (complete / no-show / cancel / reschedule) reads
// and behaves the same everywhere it appears: the prospect Demo tab, the
// Demos → Bookings tab, and the availability-calendar popup. Every status
// change is an explicit inline confirm with an optional remark, and lands on
// the booking's own timeline (like a followup activity).
// ────────────────────────────────────────────────────────────────────────────

export interface DemoTimelineEntry {
  type: string;            // 'status' | 'remark' | 'reschedule' | 'booked'
  from?: any;
  to?: any;
  note?: string;
  actorName?: string | null;
  at: string;
}

export interface DemoBooking {
  id: string;
  adminId: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string | null;
  company: string | null;
  notes: string | null;
  startAt: string;
  endAt: string;
  timezone: string;
  status: string;
  meetingLink: string | null;
  timeline?: DemoTimelineEntry[];
  // Zoom attendance/transcript (filled by platform Zoom webhooks)
  startedAt?: string | null;
  endedAt?: string | null;
  hostJoinedAt?: string | null;
  guestJoinedAt?: string | null;
  firstJoinedRole?: string | null;
  transcript?: string | null;
  transcriptStatus?: string | null;
  recordingUrl?: string | null;
}

const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  confirmed:   { label: 'Confirmed',   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed:   { label: 'Completed',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled:   { label: 'Cancelled',   cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  no_show:     { label: 'No-show',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  rescheduled: { label: 'Rescheduled', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};

/** Format an ISO instant as a short local time, e.g. "10:05 PM". */
function fmtTimeOnly(iso?: string | null): string {
  if (!iso) return '';
  return formatTime(iso);
}

export function DemoBookingCard({ booking, onChange }: { booking: DemoBooking; onChange: () => void }) {
  const [remark, setRemark] = useState('');
  const [postingRemark, setPostingRemark] = useState(false);
  const [changing, setChanging] = useState<string | null>(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [panel, setPanel] = useState<'complete' | 'no_show' | 'cancel' | null>(null);
  const [note, setNote] = useState('');
  const [regen, setRegen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const regenLink = async () => {
    setRegen(true);
    try {
      await saApi.post(`/super-admin/demo/bookings/${booking.id}/regenerate-link`);
      toast('success', 'Zoom link created');
      onChange();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Could not create Zoom link'));
    } finally {
      setRegen(false);
    }
  };

  const status = STATUS_STYLE[booking.status] ?? STATUS_STYLE.confirmed;
  const timeline = Array.isArray(booking.timeline) ? booking.timeline : [];

  const submitRemark = async () => {
    if (!remark.trim()) return;
    setPostingRemark(true);
    try {
      await saApi.post(`/super-admin/demo/bookings/${booking.id}/remark`, { remark: remark.trim() });
      setRemark('');
      toast('success', 'Remark added');
      onChange();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Could not add remark'));
    } finally {
      setPostingRemark(false);
    }
  };

  const setStatus = async (next: string) => {
    setChanging(next);
    try {
      await saApi.patch(`/super-admin/demo/bookings/${booking.id}/status`, { status: next, note: note.trim() || undefined });
      toast('success', `Marked ${STATUS_STYLE[next]?.label ?? next}`);
      setPanel(null);
      setNote('');
      onChange();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to update status'));
    } finally {
      setChanging(null);
    }
  };

  const openPanel = (p: 'complete' | 'no_show' | 'cancel') => {
    setShowReschedule(false);
    setNote('');
    setPanel(prev => (prev === p ? null : p));
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="h-1 bg-[#6366F1]" />
      <div className="p-4">
        {/* Header: title + status + Join */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-slate-900">Demo</p>
              <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border ${status.cls}`}>
                {status.label}
              </span>
            </div>
            <div className="mt-1 space-y-1 text-xs text-slate-500">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" /> {formatDateTime(booking.startAt)}</span>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <UserIcon className="h-3.5 w-3.5" /> <span className="font-medium text-slate-700">{booking.guestName}</span>
                {booking.company && <span className="text-slate-400">· {booking.company}</span>}
              </div>
              <div className="flex items-center gap-3 flex-wrap text-slate-400">
                <a href={`mailto:${booking.guestEmail}`} className="inline-flex items-center gap-1 hover:text-[#6366F1]"><Mail className="h-3.5 w-3.5" /> {booking.guestEmail}</a>
                {booking.guestPhone && <a href={`tel:${booking.guestPhone}`} className="inline-flex items-center gap-1 hover:text-[#6366F1]"><Phone className="h-3.5 w-3.5" /> {booking.guestPhone}</a>}
              </div>
            </div>
          </div>
          {booking.status === 'confirmed' && (
            booking.meetingLink ? (
              <a href={booking.meetingLink} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg flex-shrink-0">
                <Video className="h-3.5 w-3.5" /> Join
              </a>
            ) : (
              <button onClick={regenLink} disabled={regen} title="Create a Zoom meeting for this demo"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-xs font-semibold rounded-lg flex-shrink-0">
                {regen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" />} Get Zoom link
              </button>
            )
          )}
        </div>

        {/* Booking notes */}
        {booking.notes && (
          <p className="mt-2 text-xs text-slate-600 bg-slate-50 rounded px-2.5 py-1.5 italic">"{booking.notes}"</p>
        )}

        {/* Attendance (from Zoom webhooks) — actual start/end + who joined first */}
        {(booking.startedAt || booking.endedAt || booking.firstJoinedRole) && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap text-xs text-slate-500">
            <Video className="h-3.5 w-3.5 text-slate-400" />
            <span>
              {booking.startedAt && `Started ${fmtTimeOnly(booking.startedAt)}`}
              {booking.endedAt && ` · Ended ${fmtTimeOnly(booking.endedAt)}`}
              {booking.firstJoinedRole && ` · First in: ${booking.firstJoinedRole === 'host' ? 'Advisor' : 'Guest'}`}
            </span>
          </div>
        )}

        {/* Recording + transcript (Zoom cloud recording) */}
        {(booking.transcriptStatus || booking.recordingUrl) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {booking.recordingUrl && (
              <a href={booking.recordingUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline">
                <Video className="h-3.5 w-3.5" /> Recording
              </a>
            )}
            {booking.transcriptStatus === 'ready' && booking.transcript ? (
              <button type="button" onClick={() => setShowTranscript(v => !v)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded">
                <FileText className="h-3.5 w-3.5" /> {showTranscript ? 'Hide transcript' : 'View transcript'}
              </button>
            ) : booking.transcriptStatus === 'pending' ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                <FileText className="h-3.5 w-3.5" /> Transcript processing…
              </span>
            ) : null}
          </div>
        )}
        {showTranscript && booking.transcript && (
          <pre className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-700">
            {booking.transcript}
          </pre>
        )}

        {/* Timeline */}
        {timeline.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Timeline</p>
            <div className="space-y-1.5 pl-3 border-l-2 border-slate-100">
              {timeline.map((t, i) => <TimelineRow key={i} entry={t} />)}
            </div>
          </div>
        )}

        {/* Remark input */}
        {booking.status !== 'cancelled' && (
          <div className="mt-3 flex gap-2">
            <input
              value={remark}
              onChange={e => setRemark(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitRemark()}
              placeholder="Add a remark or note about this demo…"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
            />
            <button onClick={submitRemark} disabled={!remark.trim() || postingRemark}
              className="rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-xs font-semibold px-3 py-2">
              {postingRemark ? '…' : 'Add'}
            </button>
          </div>
        )}

        {/* Actions — only on the active (confirmed) booking */}
        {booking.status === 'confirmed' && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            <button onClick={() => openPanel('complete')} disabled={!!changing}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded">
              <CheckCircle2 className="h-3 w-3" /> Complete
            </button>
            <button onClick={() => { setPanel(null); setShowReschedule(v => !v); }}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded">
              <RefreshCw className="h-3 w-3" /> Reschedule
            </button>
            <button onClick={() => openPanel('no_show')} disabled={!!changing}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 px-2.5 py-1 rounded">
              No-show
            </button>
            <button onClick={() => openPanel('cancel')} disabled={!!changing}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded">
              <XCircle className="h-3 w-3" /> Cancel
            </button>
            {booking.meetingLink && (
              <button onClick={regenLink} disabled={regen} title="Create a new Zoom meeting link"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 px-2.5 py-1 rounded">
                {regen ? <Loader2 className="h-3 w-3 animate-spin" /> : <Video className="h-3 w-3" />} Regenerate link
              </button>
            )}
          </div>
        )}

        {/* Inline confirm strips */}
        {panel === 'complete' && (
          <ConfirmPanel tone="emerald" title="Completion remark (optional)" placeholder="How did the demo go? (added to the timeline)"
            note={note} setNote={setNote} busy={changing === 'completed'} confirmLabel="Mark completed"
            onConfirm={() => setStatus('completed')} onBack={() => setPanel(null)} />
        )}
        {panel === 'no_show' && (
          <ConfirmPanel tone="amber" title="Note (optional)" placeholder="Anything worth recording? (added to the timeline)"
            note={note} setNote={setNote} busy={changing === 'no_show'} confirmLabel="Confirm no-show"
            warn="Mark this demo as no-show? The status change goes to the timeline."
            onConfirm={() => setStatus('no_show')} onBack={() => setPanel(null)} />
        )}
        {panel === 'cancel' && (
          <ConfirmPanel tone="red" title="Cancel reason (optional)" placeholder="Why is this demo being cancelled?"
            note={note} setNote={setNote} busy={changing === 'cancelled'} confirmLabel="Cancel demo"
            onConfirm={() => setStatus('cancelled')} onBack={() => setPanel(null)} />
        )}

        {showReschedule && (
          <RescheduleModal booking={booking} onClose={() => setShowReschedule(false)} onDone={() => { setShowReschedule(false); onChange(); }} />
        )}
      </div>
    </div>
  );
}

function ConfirmPanel({ tone, title, placeholder, note, setNote, busy, confirmLabel, warn, onConfirm, onBack }: {
  tone: 'emerald' | 'amber' | 'red'; title: string; placeholder: string; note: string; setNote: (v: string) => void;
  busy: boolean; confirmLabel: string; warn?: string; onConfirm: () => void; onBack: () => void;
}) {
  const map = {
    emerald: { box: 'bg-emerald-50/60 border-emerald-100', ring: 'focus:ring-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    amber:   { box: 'bg-amber-50/60 border-amber-100',     ring: 'focus:ring-amber-200',   btn: 'bg-amber-600 hover:bg-amber-700' },
    red:     { box: 'bg-red-50/60 border-red-100',         ring: 'focus:ring-red-200',     btn: 'bg-red-600 hover:bg-red-700' },
  }[tone];
  return (
    <div className={`mt-3 p-3 rounded-lg border ${map.box}`}>
      {warn && <p className="text-xs text-slate-700 mb-2">{warn}</p>}
      <label className="block text-[10px] font-normal text-slate-500 uppercase mb-1">{title}</label>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder={placeholder}
        className={`w-full text-xs rounded border border-slate-200 px-2.5 py-2 resize-none focus:outline-none focus:ring-2 ${map.ring}`} />
      <div className="mt-2 flex items-center gap-2">
        <button onClick={onConfirm} disabled={busy}
          className={`inline-flex items-center gap-1 text-xs font-semibold text-white ${map.btn} disabled:opacity-50 px-3 py-1.5 rounded`}>
          {busy ? 'Saving…' : confirmLabel}
        </button>
        <button onClick={onBack} className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5">Back</button>
      </div>
    </div>
  );
}

function TimelineRow({ entry }: { entry: DemoTimelineEntry }) {
  let text: React.ReactNode = null;
  const cap = (s?: string) => (s ? s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');
  switch (entry.type) {
    case 'remark':
      text = <span className="text-slate-700">{entry.note}</span>;
      break;
    case 'status':
      text = <span className="text-slate-500">Status: {cap(entry.from)} → <b>{cap(entry.to)}</b>{entry.note ? ` — ${entry.note}` : ''}</span>;
      break;
    case 'reschedule':
      text = <span className="text-slate-500">Rescheduled → <b>{entry.to?.at ? formatDateTime(entry.to.at) : '—'}</b></span>;
      break;
    case 'link':
      text = <span className="text-slate-500">{entry.note || 'Zoom meeting link generated'}</span>;
      break;
    case 'booked':
      text = <span className="text-slate-500">Demo booked</span>;
      break;
    default:
      text = <span className="text-slate-400">{entry.type}</span>;
  }
  return (
    <div className="flex items-start gap-2 text-xs">
      <UserIcon className="h-3 w-3 text-slate-400 mt-0.5" />
      <div className="flex-1 min-w-0">
        {text}
        <p className="text-[10px] text-slate-400 mt-0.5">
          {entry.actorName ? `${entry.actorName} · ` : ''}{formatDateTime(entry.at)}
        </p>
      </div>
    </div>
  );
}

function RescheduleModal({ booking, onClose, onDone }: { booking: DemoBooking; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    saApi.get(`/public/demo/${booking.adminId}/slots`, { params: { date } })
      .then(({ data }) => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [date, booking.adminId]);

  const pick = async (start: string) => {
    setSaving(true);
    try {
      await saApi.patch(`/super-admin/demo/bookings/${booking.id}/reschedule`, { start });
      toast('success', 'Demo rescheduled');
      onDone();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to reschedule'));
    } finally {
      setSaving(false);
    }
  };

  const fmtTime = (iso: string) => formatTime(iso);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-slate-900">Reschedule — {booking.guestName}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="mb-3"><DateInput value={date} onChange={setDate} className="w-full" /></div>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
        ) : slots.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No open slots on this day.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {slots.map(s => (
              <button key={s} disabled={saving} onClick={() => pick(s)}
                className="px-2 h-10 rounded-lg border border-slate-200 hover:border-[#6366F1] hover:bg-indigo-50 disabled:opacity-50 text-sm font-medium text-slate-700">
                {fmtTime(s)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
